import { FlipHorizontal2, FlipVertical2, X } from "lucide-react";
import { useState } from "react";
import Moveable from "react-moveable";
import { useRef } from "react";
import Image from "next/image";
import { HoverBtn } from "@/components/styles";
import { lightenColor } from "@/utils/colorUtils";
import { useMediaQuery } from "@mantine/hooks";
import useUserStore, { UploadAsset } from "@/state/user/useUserStore";

export const MoveableUploadedItem: React.FC<{
  remove: any;
  hideControls?: boolean;
  color: string;
  uploadAsset: UploadAsset;
}> = ({ remove, hideControls, color, uploadAsset }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [setUploadAsset] = useUserStore((state) => [state.setUploadAsset]);
  const [frame, setFrame] = useState({
    translate: [0, 0],
    scale: [1, 1],
    width: 150 * uploadAsset.ratio,
    height: 150,
    rotate: 0,
    zIndex: 0,
  });

  const targetRef = useRef<HTMLDivElement | null>(null);
  const moveableRef = useRef<Moveable | null>(null);

  const [bounds, setBounds] = useState({
    left: 10,
    top: 10,
    right: 10,
    bottom: 10,
  });

  const [flipImage, setFlipImage] = useState(false);

  const lighterColor = lightenColor(color, 10);

  const isSmallScreen = useMediaQuery("(max-width: 768px)");

  return (
    <>
      <div
        style={{
          width: `${150 * uploadAsset.ratio}px`,
          height: `150px`,
          position: "absolute",
          right: "0",
          top: "0",
          zIndex: uploadAsset.zIndex,
          pointerEvents: hideControls ? "none" : "auto",
        }}
        onMouseEnter={() => !isSmallScreen && !hideControls && setIsHovered(true)}
        onMouseLeave={() => !isSmallScreen && !hideControls && setIsHovered(false)}
        ref={targetRef}
      >
        {isHovered && !hideControls && (
          <>
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                zIndex: 100,
              }}
            >
              <HoverBtn
                color={color}
                hoverColor={lighterColor}
                onClick={remove}
              >
                <X style={{ width: "12px", height: "12px" }} />
              </HoverBtn>
            </div>
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                zIndex: 99,
                flexDirection: "row",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "2px",
              }}
            >
              <HoverBtn
                onClick={() =>
                  setUploadAsset(uploadAsset.id, {
                    flipVertical: !uploadAsset.flipVertical,
                  })
                }
              >
                <FlipVertical2 style={{ width: "12px", height: "12px" }} />
              </HoverBtn>
              <HoverBtn
                onClick={() =>
                  setUploadAsset(uploadAsset.id, {
                    flipHorizontal: !uploadAsset.flipHorizontal,
                  })
                }
              >
                <FlipHorizontal2 style={{ width: "12px", height: "12px" }} />
              </HoverBtn>
            </div>
          </>
        )}
        <Image
          draggable="false"
          src={uploadAsset.img}
          style={{
            objectFit: "contain",
            transform: `scaleX(${uploadAsset.flipHorizontal ? -1 : 1}) scaleY(${
              uploadAsset.flipVertical ? -1 : 1
            })`,
          }}
          alt=""
          fill
          sizes="50vw"
        />
      </div>
      <Moveable
        ref={moveableRef}
        target={hideControls ? null : targetRef.current}
        draggable={true}
        resizable={true}
        scalable={true}
        rotatable={true}
        onDrag={({ beforeTranslate }) => {
          frame.translate = beforeTranslate;
        }}
        onResize={({ width, height }) => {
          frame.width = width;
          frame.height = height;
        }}
        onScale={({ scale }) => {
          frame.scale = scale;
        }}
        bounds={bounds}
        onRotate={({ beforeRotate }) => {
          frame.rotate = beforeRotate;
        }}
        onRender={({ target }) => {
          target.style.transform = `translate(${frame.translate[0]}px, ${frame.translate[1]}px) rotate(${frame.rotate}deg)`;
          target.style.width = `${frame.width}px`;
          target.style.height = `${frame.height}px`;
        }}
      />
    </>
  );
};
